/**
 * The one conversion this section makes, named so it is visible in a diff.
 *
 * ## Why a percentage may become a `number` when a price may not
 *
 * `money.ts` states the rule and the reason: *a price stays a `string` from the wire to the
 * pixel*, because `Number("0.1") + Number("0.2")` is `0.30000000000000004` and a price the
 * operator reads, copies into an order or compares against a limit must not have been through
 * that. Every price in this section obeys it — `ScreenerRow.price` and `Fundamental.value` are
 * rendered as the strings they arrived as, and nothing here parses them.
 *
 * ⚠ **An excess return is not a price.** It is a statistic the server already computed by
 * division, it is displayed to one decimal place, and there is no path from it to an order —
 * this section has no order path at all. What it needs from `Num` is the thing `Num` gives only
 * to numbers: the sign, the P&L colour and the `▲`/`▼`. `formatNum` returns a string untouched,
 * so a string percentage renders as a bare `-12.0` with no sign, no colour and no arrow — which
 * is how this shipped for one round of review, and it read as a *quantity* rather than a loss.
 *
 * `FleetScreen` already makes the same conversion for `net_pct`. This is the same call with the
 * parse done by `Decimal` rather than by `Number`, so a malformed value **throws** instead of
 * rendering `NaN%` in a P&L column.
 */

import { dec } from '../../money';

/** A signed decimal percent from the API as a `number` for `Num`. `null` passes through. */
export function asPercent(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  return dec(value).toNumber();
}
